import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useState } from "react";
import { listBackfillTargets } from "@/lib/media-library.functions";
import { runBackfill, type BackfillTarget, type BackfillProgress } from "@/lib/backfill-variants";

export const Route = createFileRoute("/_authenticated/dashboard/image-variants")({
  component: ImageVariantsPage,
});

function ImageVariantsPage() {
  const list = useServerFn(listBackfillTargets);
  const [loading, setLoading] = useState(true);
  const [targets, setTargets] = useState<BackfillTarget[]>([]);
  const [totals, setTotals] = useState<{ media: number; slots: number; projectPhotos: number; images: number }>({
    media: 0,
    slots: 0,
    projectPhotos: 0,
    images: 0,
  });
  const [progress, setProgress] = useState<BackfillProgress | null>(null);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await list();
        setTotals(r.totals);
        const built: BackfillTarget[] = [];
        for (const m of r.media) {
          built.push({ kind: "media", id: m.id, label: `media/${m.filename}`, sourceUrl: m.webpUrl });
        }
        for (const s of r.slots) {
          built.push({ kind: "slot", id: s.id, label: `slot/${s.slotKey}`, sourceUrl: s.webpUrl });
        }
        for (const p of r.projects) {
          for (const u of p.urls) {
            built.push({
              kind: "project",
              projectId: p.projectId,
              url: u,
              label: `project/${p.projectName}`,
              sourceUrl: u,
            });
          }
        }
        setTargets(built);
      } catch (e: any) {
        setErr(e?.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, [list]);

  const summary = useMemo(
    () =>
      `${totals.images} image${totals.images === 1 ? "" : "s"} will be processed ` +
      `(${totals.media} media library · ${totals.slots} slots · ${totals.projectPhotos} project photos).`,
    [totals],
  );

  const start = async () => {
    if (running || targets.length === 0) return;
    setRunning(true);
    setProgress({ done: 0, total: targets.length, currentLabel: "", ok: 0, skipped: 0, failed: 0, log: [] });
    await runBackfill(targets, setProgress);
    setRunning(false);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <header>
        <h1 className="font-display text-2xl font-bold text-ink">Image variants backfill</h1>
        <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
          Generates 400/800/1200/1600/2400-wide WebP variants next to each stored image so the
          site can serve the correct size per viewport. Existing originals are never overwritten
          or deleted — variants are additive.
        </p>
      </header>

      {loading ? (
        <p className="text-sm text-muted-foreground">Counting targets…</p>
      ) : err ? (
        <p className="text-sm text-destructive">{err}</p>
      ) : (
        <>
          <div className="rounded-lg border border-border bg-card p-4">
            <p className="text-sm text-ink font-medium">{summary}</p>
            <button
              onClick={start}
              disabled={running || targets.length === 0}
              className="mt-4 px-4 py-2 rounded-lg bg-brand text-primary-foreground font-semibold disabled:opacity-50"
            >
              {running
                ? "Running…"
                : targets.length === 0
                  ? "Nothing to backfill"
                  : "Run backfill"}
            </button>
          </div>

          {progress && (
            <div className="rounded-lg border border-border bg-card p-4 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-ink">
                  {progress.done} / {progress.total}
                </span>
                <span className="text-muted-foreground">
                  ok {progress.ok} · skipped {progress.skipped} · failed {progress.failed}
                </span>
              </div>
              <div className="h-2 w-full bg-muted rounded overflow-hidden">
                <div
                  className="h-full bg-brand transition-all"
                  style={{
                    width: `${progress.total === 0 ? 0 : (progress.done / progress.total) * 100}%`,
                  }}
                />
              </div>
              {progress.currentLabel && running && (
                <p className="text-xs text-muted-foreground">Working on: {progress.currentLabel}</p>
              )}
              <pre className="max-h-64 overflow-auto bg-background p-3 rounded text-[11px] font-mono leading-relaxed">
                {progress.log.slice(-200).join("\n")}
              </pre>
            </div>
          )}
        </>
      )}
    </div>
  );
}
